import type { Map } from 'immutable';
import type { ServiceUrlObject } from '@common/interfaces/serviceUrlObject';
import type { IDiagnosticReportGetOne } from '../../interfaces/diagnosticReportGetOne';
import type { IProcedureGetOne } from '../../interfaces/procedureGetOne';
import type { IProcess } from '../../interfaces/process';
import type { IRequestGetOne } from '../../interfaces/requestGetOne';
import type { ISupervisionGetOne } from '../../interfaces/supervisionGetOne';
import type { IItemState, IReportItemState } from '../episodes/Episode';
import { CustomControlEntity } from '../../../../common/enums/customControlEntity';
import { ProcessScrollItem } from '../../../../common/enums/processScrollItem';
import { fetchJson } from '../../../store/fetch';
import { Loading, LoadingTwo } from '../../elements/common';
import { Component } from '../../elements/Component';
import { loadCustomControl } from '../customControls/loadCustomControl';
import { ProcessItemScrollElement } from '../episodes/common';
import { EpisodeContext } from '../episodes/EpisodeContext';
import { ReloadEpisode } from '../episodes/reloadEpisode';
import { ProcedureItemAddEdit } from './ProcedureItemAddEdit';

interface IProcedureItemProps {
    addExaminationReport?: () => Promise<void>;
    disableFormAutosave: boolean;
    formId: string;
    isBiometrist?: boolean;
    isFirstItem: boolean;
    isLastItem: boolean;
    isPreviousRequest: boolean;
    itemState: IItemState;
    onLoaded: () => void;
    onReloadPatient?: () => Promise<void>;
    onServiceIdSelected?: (args: ServiceUrlObject) => Promise<void>;
    organizationIsPerformerProcedureAndReport: boolean;
    process: IProcess;
    reloadSelfOrAllProcesses: (reload: ReloadEpisode) => Promise<void>;
    removeProcess: () => Promise<void>;
    reportItemState: Map<string, IReportItemState>;
    scrollToProcessItem: (requestId: string, scrollItem: ProcessScrollItem) => Promise<void>;
    setHideCreateActions: (value: boolean) => Promise<void>;
    updateItemState: (itemState?: IItemState) => Promise<void>;
    updateReportItemState: (id: string, reportItemState?: IReportItemState) => Promise<void>;
    updateSupervision?: () => Promise<void>;
}

interface IProcedureItemState {
    diagnosticReport?: IDiagnosticReportGetOne;
    isShowMoreVisible: boolean;
    procedure?: IProcedureGetOne;
    request?: IRequestGetOne;
    supervision?: ISupervisionGetOne;
}

export class ProcedureItem extends Component<IProcedureItemProps, IProcedureItemState> {
    constructor(props: IProcedureItemProps, context?: IProcedureItemState) {
        super(props, context);
        this.state = {
            isShowMoreVisible: true,
        };
    }

    public async componentDidMount() {
        if (this.props.itemState.mode === 'add') {
            await this.loadRequest(this.props.process.id);
        } else {
            await this.load(this.props.process);
        }

        this.props.onLoaded();
    }

    public async componentDidUpdate(prevProps: Readonly<IProcedureItemProps>) {
        if (
            (!this.props.itemState.initialIsOpen && prevProps.itemState.initialIsOpen) ||
            prevProps.process.status !== this.props.process.status ||
            prevProps.process.diagnosticReports.length !== this.props.process.diagnosticReports.length ||
            (prevProps.process.procedure &&
                this.props.process.procedure &&
                prevProps.process.procedure.performerPractitionerId !==
                    this.props.process.procedure.performerPractitionerId) ||
            this.props.process.serviceId !== prevProps.process.serviceId
        ) {
            await this.load(this.props.process);
        } else if (
            this.props.process.supervision &&
            this.props.process.supervision.id &&
            (!prevProps.process.supervision || !prevProps.process.supervision.id)
        ) {
            await this.loadSupervision(this.props.process.supervision.id);
        }
    }

    public render() {
        return (
            <EpisodeContext.Consumer>
                {(context) => (
                    <>
                        <ProcessItemScrollElement
                            item={ProcessScrollItem.procedure}
                            requestId={this.props.process.id}
                        />
                        {this.props.itemState.mode !== 'add' && (
                            <LoadingTwo
                                item1={this.state.procedure}
                                item2={this.state.request}
                                loaded={(procedure, request) => (
                                    <>
                                        {this.props.itemState.mode === 'read' && (
                                            <>{this.renderCustomControls(procedure, request)}</>
                                        )}
                                        {this.props.itemState.mode === 'edit' && (
                                            <ProcedureItemAddEdit
                                                diagnosticReport={this.state.diagnosticReport}
                                                disableFormAutosave={this.props.disableFormAutosave}
                                                formId={this.props.formId}
                                                isFirstItem={this.props.isFirstItem}
                                                isLastItem={this.props.isLastItem}
                                                itemState={this.props.itemState}
                                                onCancel={async () => await this.changeMode('read')}
                                                onReloadPatient={this.props.onReloadPatient}
                                                onSave={async () => {
                                                    await this.props.reloadSelfOrAllProcesses(ReloadEpisode.reloadSelf);
                                                    await this.load(this.props.process);
                                                    await this.changeMode('read');
                                                    await this.props.reloadSelfOrAllProcesses(ReloadEpisode.reloadAll);
                                                    context?.changeShowRecommendations(true);
                                                }}
                                                organizationIsPerformerProcedureAndReport={
                                                    this.props.organizationIsPerformerProcedureAndReport
                                                }
                                                procedure={procedure}
                                                process={this.props.process}
                                                reloadProcess={this.props.reloadSelfOrAllProcesses}
                                                removeProcedure={async () => {
                                                    await this.props.updateItemState(this.props.itemState);
                                                    await this.props.reloadSelfOrAllProcesses(ReloadEpisode.reloadSelf);
                                                }}
                                                request={request}
                                                serviceId={this.props.process.serviceId}
                                            />
                                        )}
                                    </>
                                )}
                            />
                        )}
                        {this.props.itemState.mode === 'add' && (
                            <Loading
                                item={this.state.request}
                                loaded={(request) => (
                                    <ProcedureItemAddEdit
                                        disableFormAutosave={this.props.disableFormAutosave}
                                        formId={this.props.formId}
                                        isFirstItem={this.props.isFirstItem}
                                        isLastItem={this.props.isLastItem}
                                        itemState={this.props.itemState}
                                        onCancel={async () => await this.props.updateItemState()}
                                        onReloadPatient={this.props.onReloadPatient}
                                        onSave={async () => {
                                            await this.props.reloadSelfOrAllProcesses(ReloadEpisode.reloadSelf);
                                            await this.load(this.props.process);
                                            await this.changeMode('read');
                                            await this.props.reloadSelfOrAllProcesses(ReloadEpisode.reloadAll);
                                            context?.changeShowRecommendations(true);
                                        }}
                                        organizationIsPerformerProcedureAndReport={
                                            this.props.organizationIsPerformerProcedureAndReport
                                        }
                                        process={this.props.process}
                                        reloadProcess={this.props.reloadSelfOrAllProcesses}
                                        removeProcedure={async () => {
                                            await this.props.updateItemState();
                                            await this.props.reloadSelfOrAllProcesses(ReloadEpisode.reloadSelf);
                                        }}
                                        request={request}
                                        serviceId={this.props.process.serviceId}
                                    />
                                )}
                            />
                        )}
                    </>
                )}
            </EpisodeContext.Consumer>
        );
    }

    private renderCustomControls(procedure: IProcedureGetOne, request: IRequestGetOne) {
        if (
            this.props.organizationIsPerformerProcedureAndReport &&
            this.state.diagnosticReport &&
            this.state.diagnosticReport.customControlData !== undefined
        ) {
            return this.renderProcedureDiagnosticReportCustomControl(procedure, request);
        } else {
            return this.renderProcedureCustomControl(procedure, request);
        }
    }

    private renderProcedureCustomControl(procedure: IProcedureGetOne, request: IRequestGetOne) {
        const CustomControl = loadCustomControl(
            request.customControlVersion,
            CustomControlEntity.ProcedureRead,
            request.service.code,
            request.service.serviceType
        );

        return (
            <CustomControl
                addExaminationReport={this.props.addExaminationReport}
                editProcedure={async () => await this.changeMode('edit')}
                isBiometrist={this.props.isBiometrist}
                isFirstItem={this.props.isFirstItem}
                isLastItem={this.props.isLastItem}
                itemState={this.props.itemState}
                procedure={procedure}
                process={this.props.process}
                reloadProcess={async () => {
                    await this.load(this.props.process);
                    await this.props.reloadSelfOrAllProcesses(ReloadEpisode.reloadSelf);
                }}
                removeProcedure={async () => {
                    if (this.state.request && this.state.request.isDirectProcedureAdded) {
                        await this.props.removeProcess();
                    } else {
                        await this.props.reloadSelfOrAllProcesses(ReloadEpisode.reloadSelf);
                    }
                }}
                request={request}
                serviceCode={request.service.code}
                supervision={this.state.supervision}
                task={this.props.process.task}
                updateItemState={this.props.updateItemState}
                updateSupervision={this.props.updateSupervision}
            />
        );
    }

    private renderProcedureDiagnosticReportCustomControl(procedure: IProcedureGetOne, request: IRequestGetOne) {
        if (!this.state.diagnosticReport || !this.props.reportItemState || !this.props.updateReportItemState) {
            return null;
        }

        const CustomControl = loadCustomControl(
            request.customControlVersion,
            CustomControlEntity.ProcedureDiagnosticReportRead,
            request.service.code,
            request.service.serviceType
        );

        return (
            <EpisodeContext.Consumer>
                {(context) =>
                    this.state.diagnosticReport && (
                        <CustomControl
                            acceptDiagnosticReport={async () => {
                                await this.load(this.props.process);
                                await this.props.reloadSelfOrAllProcesses(ReloadEpisode.reloadAll);
                                context?.changeShowRecommendations(true);
                            }}
                            diagnosticReport={this.state.diagnosticReport}
                            editProcedure={async () => await this.changeMode('edit')}
                            isFirstItem={this.props.isFirstItem}
                            isLastItem={this.props.isLastItem}
                            isPreviousRequest={this.props.isPreviousRequest}
                            itemState={this.props.itemState}
                            onServiceIdSelected={this.props.onServiceIdSelected}
                            procedure={procedure}
                            process={this.props.process}
                            reloadProcess={async (mode?: ReloadEpisode) => {
                                await this.load(this.props.process);
                                await this.props.reloadSelfOrAllProcesses(mode ? mode : ReloadEpisode.reloadSelf);
                            }}
                            removeProcedure={async () => {
                                if (
                                    this.props.organizationIsPerformerProcedureAndReport &&
                                    this.state.request &&
                                    this.state.request.isDirectProcedureAdded
                                ) {
                                    await this.props.removeProcess();
                                } else {
                                    await this.props.updateItemState(this.props.itemState);
                                    await this.props.reloadSelfOrAllProcesses(ReloadEpisode.reloadSelf);
                                }
                            }}
                            reportItemState={this.props.reportItemState.get(this.state.diagnosticReport.id)}
                            request={request}
                            scrollToProcessItem={this.props.scrollToProcessItem}
                            serviceCode={request.service.code}
                            setHideCreateActions={this.props.setHideCreateActions}
                            supervision={this.state.supervision}
                            task={this.props.process.task}
                            updateItemState={this.props.updateItemState}
                            updateReportItemState={async (reportItemState) =>
                                await this.props.updateReportItemState(this.state.diagnosticReport!.id, reportItemState)
                            }
                            updateSupervision={this.props.updateSupervision}
                        />
                    )
                }
            </EpisodeContext.Consumer>
        );
    }

    private async changeMode(mode: 'edit' | 'read') {
        await this.props.updateItemState({ ...this.props.itemState, mode: mode });

        if (mode === 'edit') {
            await this.props.scrollToProcessItem(this.props.process.id, ProcessScrollItem.procedure);
        }
    }

    private async load(process: IProcess) {
        await this.loadRequest(process.id);
        await this.loadProcedure(process.id);
        if (this.props.organizationIsPerformerProcedureAndReport) {
            await this.loadDiagnosticReport(process);
        }
        if (process.supervision && process.supervision.id) {
            await this.loadSupervision(process.supervision.id);
        }
    }

    private async loadRequest(requestId: string) {
        const url =
            `/api/requests/${requestId}?showAssets=true` +
            '&includeItemAuthorizations=' +
            'canAddExaminationDiagnosticReport,' +
            'canBecomeRequester,' +
            'canAddReferral';

        const result = await fetchJson(url, 'GET');
        await this.setState({ request: result.json });
    }

    private async loadSupervision(supervisionId: string) {
        if (this.props.itemState.mode !== 'add') {
            const url =
                `/api/supervisions/${supervisionId}` +
                '?includeItemAuthorizations=' +
                'canUpdateSupervision,' +
                'canTakeSupervision';
            const result = await fetchJson(url, 'GET');
            await this.setState({ supervision: result.json });
        }
    }

    private async loadProcedure(requestId: string) {
        if (this.props.itemState.mode !== 'add') {
            const url =
                `/api/requests/${requestId}/procedure?showAssets=true` +
                '&includeItemAuthorizations=' +
                'canTakeProcess,' +
                'canDelete,' +
                'canUpdateProcedure,' +
                'canTakeAnalysis,' +
                'canTakeAnalysisFromColleague,' +
                'canUpdateProcedureToCancelled';
            const result = await fetchJson(url, 'GET');
            await this.setState({ procedure: result.json });
        }
    }

    private async loadDiagnosticReport(process: IProcess) {
        if (process.diagnosticReports.length === 1) {
            const url =
                `/api/diagnosticReports/${process.diagnosticReports[0].id}?showAssets=true&includeItemAuthorizations=` +
                'canAddDiagnosticReportClarificationRequester,' +
                'canAddDiagnosticReportClarificationPerformer,' +
                'canUpdateDiagnosticReportToClosed,' +
                'canTakeProcess';

            const result = await fetchJson(url, 'GET');
            await this.setState({ diagnosticReport: result.json });
        }
    }
}
